import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Compass, Loader2 } from "lucide-react";
import { GoogleIcon } from "@/components/GoogleIcon";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex relative bg-primary text-white flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-50" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <img src="/noventra-logo.png" alt="Noventra Academic Press" className="w-9 h-9 rounded-lg object-cover bg-white" />
          <span className="font-serif font-bold text-lg">Noventra Academic Press</span>
        </Link>
        <div className="relative">
          <h2 className="font-serif font-black text-4xl leading-tight">Knowledge, <br />open to all.</h2>
          <p className="mt-4 text-slate-300 max-w-sm">Sign in to submit manuscripts, track peer review, and manage your editorial work.</p>
        </div>
        <p className="relative text-xs text-slate-400 font-mono">© 2026 Noventra Academic Press</p>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h1 className="font-serif font-bold text-3xl text-slate-900 dark:text-slate-100">Sign in</h1>
          <p className="text-slate-500 mt-2 text-sm">Access your Noventra account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="you@institution.edu" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="••••••••" />
            </div>
            {err && <p data-testid="login-error" className="text-sm text-destructive">{err}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full rounded-full h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200 dark:border-slate-700" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white dark:bg-slate-900 px-2 text-slate-400">or</span></div>
          </div>
          <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-login-btn" className="w-full rounded-full h-11 gap-2">
            <GoogleIcon /> Continue with Google
          </Button>

          <p className="mt-6 text-sm text-slate-500 text-center">
            New here? <Link to="/register" data-testid="to-register" className="text-primary dark:text-sky-400 font-medium hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
